import { IGame, IOutcome } from '@/api/games';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { BigNumber } from 'bignumber.js';

export const FORMAT: BigNumber.Format = {
  // string to prepend
  prefix: '',
  // decimal separator
  decimalSeparator: '.',
  // grouping separator of the integer part
  groupSeparator: '',
  // primary grouping size of the integer part
  groupSize: 3,
  // secondary grouping size of the integer part
  secondaryGroupSize: 0,
  // grouping separator of the fraction part
  fractionGroupSeparator: ' ',
  // grouping size of the fraction part
  fractionGroupSize: 0,
  // string to append
  suffix: '',
};

export const BN = BigNumber.clone({
  FORMAT,
  DECIMAL_PLACES: 9,
  ROUNDING_MODE: BigNumber.ROUND_DOWN,
});

export const DP = 3;

export const formatLamports = (val: BigNumber.Value) => {
  return new BN(val).times(LAMPORTS_PER_SOL).toString();
};

export const parseLamports = (val: BigNumber.Value) => {
  return new BN(val).div(LAMPORTS_PER_SOL).dp(DP);
};

export const calcPayout = (data: {
  game: IGame;
  outcome: IOutcome;
  stakes: Record<string, string>;
  prevStakes: Record<string, string>;
}) => {
  const { stakes, prevStakes, game, outcome } = data;

  const stake = new BN(stakes[outcome.frog.id]);
  const prevStake = new BN(prevStakes?.[outcome.frog.id] || '0');
  if (stake.eq(0) || stake.isNaN()) {
    return new BN(0);
  }

  const stakeLamports = stake.times(LAMPORTS_PER_SOL).plus(prevStake);
  const poolStake = new BN(game.totalStake);

  let totalStake = new BN(0);
  for (const key in stakes) {
    const val = new BN(stakes[key]);
    if (val.isNaN()) continue;

    totalStake = totalStake.plus(val.times(LAMPORTS_PER_SOL));
  }

  if (poolStake.eq(0)) {
    return stake;
  }

  const poolAfterFee = poolStake
    .plus(totalStake)
    .times(100 - game.fee)
    .div(100);
  const outcomeStake = totalStake.plus(outcome.totalStake);

  const payout = stakeLamports.times(poolAfterFee).div(outcomeStake);
  return payout.div(LAMPORTS_PER_SOL);
};

export const calcInstantPayout = (data: {
  stake: BigNumber.Value;
  odds: number | string;
}) => {
  const { stake, odds } = data;
  if (!stake || !odds) return new BN(0);

  return new BN(stake).times(odds);
};
